/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { useFormContext } from 'react-hook-form';
import { SageBreakpointProvider, SageTokens } from '@kajabi/sage-react';
import usePopupCheckout from './hooks/usePopupCheckout';
import PopupCheckoutModal from './PopupCheckoutModal';

// Need to import i18n to make sure it's loaded
// eslint-disable-next-line no-unused-vars
import i18next from './i18n';
import './styles/style.scss';
import { STRIPE_APPEARANCE, FONTS } from './constants';

function PopupCheckout({ el, offerToken, enableModal }) {
  const {
    offerDataLoaded,
    modalActive,
    handleShowModal,
    handleHideModal,
    displayPrice,
    displayOverridePrice,
    dueNowPriceInCents,
    showSaveCardCheckbox,
    saveCardDefaultValue,
    isOfferUnavailable,
    isOfferFree,
    submitForm,
    siteUserSignedIn,
    taxLoading,
    isCouponLoading,
    couponError,
    aboveQuantityLimit,
  } = usePopupCheckout(offerToken, enableModal);
  const shouldRenderModal = modalActive && offerDataLoaded;
  const triggerElement = React.useMemo(
    () => (
      <span
        tabIndex="0"
        role="button"
        aria-label="Open"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: el.toString() }}
        onClick={handleShowModal}
        // using onMouseUp to avoid the stopPropagation in the exit and two step popup modals
        // we'll need to remove this in the theme if we ever want to strictly use onClick
        // see exit_pop.js and two_step_popup.js in the encore site theme for more info
        onMouseUp={handleShowModal}
        onKeyDown={null}
      />
    ),
    [el, handleShowModal],
  );
  // if the offer requires the stripe element, wrap it in the Elements component
  const { getValues } = useFormContext();
  const { offer, price, stripe, additionalPaymentMethods } = getValues();

  const elementWrap = React.useCallback(
    (content) => {
      if (stripe) {
        const options = {
          fonts: FONTS,
          appearance: STRIPE_APPEARANCE,
          mode: 'payment',
          // too low a value will cause the stripe element to fail, so set it to this
          // amount for now, it will be overridden before the actual element is displayed
          // The value depends on the settlement currency of the hero's stripe account
          amount: Math.max(dueNowPriceInCents, 100),
          currency: price.currency.toLowerCase(),
          paymentMethodTypes: additionalPaymentMethods
            ? [
                'card',
                ...additionalPaymentMethods.filter(
                  // don't include apple_pay or google_pay in the list of payment methods
                  // they're handled by the wallets parameter
                  (method) => method !== 'apple_pay' && method !== 'google_pay',
                ),
              ]
            : ['card'],
          externalPaymentMethodTypes: offer.paymentMethods?.includes('paypal')
            ? ['external_paypal']
            : [],
        };
        if (offer.vendorAccountId) {
          options.onBehalfOf = offer.vendorAccountId;
        }
        return (
          <Elements stripe={stripe} options={options}>
            {content}
          </Elements>
        );
      }
      return content;
    },
    [offer, price, stripe, dueNowPriceInCents, additionalPaymentMethods],
  );

  return (
    <>
      {shouldRenderModal &&
        ReactDOM.createPortal(
          elementWrap(
            <SageBreakpointProvider queries={SageTokens.BREAKPOINT_QUERIES}>
              <PopupCheckoutModal
                active={modalActive}
                displayPrice={displayPrice}
                displayOverridePrice={displayOverridePrice}
                dueNowPriceInCents={dueNowPriceInCents}
                isOfferUnavailable={isOfferUnavailable}
                showSaveCardCheckbox={showSaveCardCheckbox}
                saveCardDefaultValue={saveCardDefaultValue}
                isOfferFree={isOfferFree}
                submitForm={submitForm}
                handleHideModal={handleHideModal}
                siteUserSignedIn={siteUserSignedIn}
                taxLoading={taxLoading}
                isCouponLoading={isCouponLoading}
                couponError={couponError}
                aboveQuantityLimit={aboveQuantityLimit}
              />
            </SageBreakpointProvider>,
          ),
          document.body,
        )}
      {triggerElement}
    </>
  );
}

PopupCheckout.propTypes = {
  el: PropTypes.string.isRequired,
  offerToken: PropTypes.string.isRequired,
  enableModal: PropTypes.bool,
};

PopupCheckout.defaultProps = {
  enableModal: true,
};

export default PopupCheckout;
